/* eslint-disable max-lines-per-function, complexity, @typescript-eslint/no-explicit-any,
   @typescript-eslint/no-non-null-assertion, max-len, no-console */
import type { SupabaseClient } from '@supabase/supabase-js';
import { getProdSupabaseClient } from '@/lib/supabase/client';
import { isTenantAccessError, TenantAccessError } from '@/lib/supabase/rpcErrors';

// The prod client is created without generated Database types, so its rpc()
// calls infer `never`. These RPCs return plain JSON shaped by the SQL functions.
const getRecallClient = () => getProdSupabaseClient() as unknown as SupabaseClient;

export interface RecallVehicle {
  id: string;
  vin: string;
  carYear: number;
  makeName: string;
  modelName: string;
  seller_name: string;
  seller_address: string;
  campaign_number: string;
  recall_title: string;
  dealer_street: string;
  dealer_city: string;
  dealer_region: string;
  dealer_postal_code: string;
  dealer_latitude: number | null;
  dealer_longitude: number | null;
  dealer_phone: string;
  dealer_type: string;
  dealer_inventory_url: string;
  recall_type: string;
  recall_status: string;
  recall_description: string;
  car_trim: string;
  car_price: number | null;
  car_mileage: number | null;
  dealer_rating: number | null;
  safety_recall_count: number;
  created_at: string | null;
  listing_title: string;
  exterior_color: string;
  transmission: string;
  engine: string;
  body_type: string;
  main_picture_url: string;
  picture_count: number;
  possible_recall: boolean;
  is_relevant_recall: boolean;
  sales_status: string;
  sold_date: string | null;
  has_advance_notice_recalls: boolean;
  stock_number?: string;
  // Additional fields for compatibility
  priority: number;
  mobile_eligible: boolean;
  coordinates?:
    | {
        latitude: number;
        longitude: number;
      }
    | undefined;
  // Legacy field names for backward compatibility
  make: string;
  model: string;
  year: number;
  seller_city: string;
  seller_region: string;
  seller_postal_code: string;
  campaign_title: string;
  recall_date: string;
}

export interface RecallFilters {
  vin?: string | undefined;
  make?: string | undefined;
  model?: string | undefined;
  year?: number | undefined;
  dealer?: string | undefined;
  campaign?: string | undefined;
  campaigns?: string[] | undefined;
  campaign_status?: string | undefined;
  priority?: number | undefined;
  mobile_eligible?: boolean | undefined;
  assignment_status?: string | undefined;
  location?:
    | {
        city?: string | undefined;
        region?: string | undefined;
        zipCode?: string | undefined;
      }
    | undefined;
  dateRange?:
    | {
        start?: string | undefined;
        end?: string | undefined;
      }
    | undefined;
}

export interface RecallListResponse {
  recalls: RecallVehicle[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface RecallStats {
  totalRecalls: number;
  byPriority: Record<number, number>;
  byMake: Record<string, number>;
  byRegion: Record<string, number>;
  mobileEligible: number;
  recentRecalls: number;
}

// Note: Removed convertFilters function - no longer needed with direct RPC calls

// Note: Removed client-side cache implementation
// Server-side pagination with secure RPC functions eliminates the need for client caching

// Note: Removed executeRecallQuery helper function
// All queries now go through secure RPC functions

/**
 * Fetches (vin, campaign_number) pairs that have been completed or cancelled,
 * using the completed_recalls view (same source the map page uses).
 * The view unions technician_assignments + recall_lifecycle_records,
 * covering silent lifecycle write failures. No tenant filter needed —
 * RLS on underlying tables enforces isolation, and zones are exclusive per tenant.
 */
async function getExcludedRecallPairs(client: SupabaseClient): Promise<Set<string>> {
  const { data } = await client.from('completed_recalls').select('vin, campaign_number');

  const excluded = new Set<string>();
  for (const r of (data || []) as Array<{ vin: string; campaign_number: string }>) {
    excluded.add(`${r.vin}-${r.campaign_number}`);
  }
  return excluded;
}

export const recallAPI = {
  // Get paginated recall list with advanced filtering - optimized for performance

  async getRecallList(
    filters: RecallFilters = {},
    page: number = 1,
    pageSize: number = 25,
    userId: string | undefined,
    tenantId: string
  ): Promise<RecallListResponse> {
    // Validate tenantId before proceeding
    if (!tenantId || tenantId.trim() === '') {
      throw new Error('Tenant ID is required to fetch recalls');
    }

    // Import rate limiter
    // const { checkRateLimit, RateLimitConfigs } = await import('@/lib/utils/rateLimiter');

    try {
      // Temporarily disable rate limiting to debug authentication issues
      // if (userId) {
      //   await checkRateLimit(userId, RateLimitConfigs.recallList);
      // }

      // Cap page size to prevent excessive data loading
      const cappedPageSize = Math.min(pageSize, 100);

      // Temporary fallback to old method to prevent logout issues
      // TODO: Re-enable RPC functions after debugging authentication issues
      const prodClient = getRecallClient();

      // Check if tenant is internal (bypasses zone/zip filtering)
      const { isInternalTenant } = await import('@/lib/supabase/tenantScoping');
      const tenantIsInternal = await isInternalTenant(prodClient, tenantId);

      let rpcData: any;
      let rpcError: any;

      if (tenantIsInternal) {
        // For internal tenants, use get_recalls_for_viewport_latest_per_zone
        // This filters to latest snapshot_date per zone_slug to prevent double counting
        // Pass p_snapshot_date as NULL to let the RPC compute latest date per zone_slug
        // (not a universal max date, since different zones may refresh on different dates)

        // Fetch all data using the new RPC that filters by latest snapshot_date per zone_slug
        const { data: viewportData, error: viewportError } = await prodClient.rpc(
          'get_recalls_for_viewport_latest_per_zone',
          {
            p_south_lat: null, // No viewport bounds - get all data
            p_north_lat: null,
            p_west_lng: null,
            p_east_lng: null,
            p_snapshot_date: null, // NULL = compute latest date per zone_slug automatically
            p_limit: 100000, // Large limit to get all data
            p_offset: 0
          }
        );

        if (viewportError) {
          // If the new RPC doesn't exist, fall back to the old one and apply client-side filtering
          if (viewportError.message?.includes('function') || viewportError.code === '42883') {
            console.warn(
              '[recallAPI] get_recalls_for_viewport_latest_per_zone not found, falling back to get_recalls_for_viewport with client-side filtering'
            );
            const fallbackResult = await prodClient.rpc('get_recalls_for_viewport', {
              p_south_lat: null,
              p_north_lat: null,
              p_west_lng: null,
              p_east_lng: null,
              p_snapshot_date: null, // Get all dates, we'll filter client-side
              p_limit: 100000,
              p_offset: 0
            });
            rpcError = fallbackResult.error;
            if (!rpcError) {
              // Apply client-side filtering for latest snapshot_date per zone_slug
              const { filterRecallsByLatestSnapshotPerZoneSlug } = await import(
                '@/features/admin/utils/filterRecallsByLatestSnapshot'
              );
              const filteredData = await filterRecallsByLatestSnapshotPerZoneSlug(
                fallbackResult.data || []
              );

              rpcData = {
                data: filteredData,
                total_count: filteredData.length,
                page: page,
                page_size: cappedPageSize,
                total_pages: 0
              };
              (rpcData as any)._internalTenantFilters = filters;
            }
          } else {
            rpcError = viewportError;
          }
        } else {
          // For internal tenants, we need to:
          // 1. Expand safety_recalls arrays if needed (handled by existing code)
          // 2. Apply filters
          // 3. Paginate
          // Format response to match get_paginated_recalls_secure format
          // The expansion and filtering will be handled by the existing code below
          rpcData = {
            data: viewportData || [],
            total_count: (viewportData || []).length, // Will be recalculated after expansion/filtering
            page: page,
            page_size: cappedPageSize,
            total_pages: 0 // Will be recalculated after expansion/filtering
          };

          // Note: We'll apply filters and pagination after expansion in the code below
          // Store filters for later use
          (rpcData as any)._internalTenantFilters = filters;
        }
      } else {
        // For dealer tenants, use get_paginated_recalls_secure (with zone/zip filtering)
        // The RPC function now filters to latest snapshot_date per zone_slug server-side
        // Use the working RPC function directly
        // NOTE: The RPC function now expands safety_recalls array (see migration:
        // fix-rpc-expand-safety-recalls-array-v2.sql)
        // Build p_filters: include campaign (legacy) when we have campaigns for backward compat
        // so single-select works even before migration 20260307120000 is applied
        const rpcFilters = { ...filters };
        if (filters.campaigns?.length && !filters.campaign) {
          rpcFilters.campaign = filters.campaigns[0];
        }
        const result = await prodClient.rpc('get_paginated_recalls_secure', {
          p_tenant_id: tenantId,
          p_filters: rpcFilters,
          p_page: page,
          p_page_size: cappedPageSize,
          p_user_id: userId ?? null
        });
        rpcData = result.data;
        rpcError = result.error;
      }

      if (rpcError) {
        // PT401 / 'tenant': the session's tenant is not the requested one.
        if (isTenantAccessError(rpcError)) throw new TenantAccessError();
        throw new Error(`Failed to fetch recalls: ${rpcError.message}`);
      }

      if (!rpcData) {
        return {
          recalls: [],
          total: 0,
          page,
          pageSize: cappedPageSize,
          totalPages: 0
        };
      }

      // Extract data from the RPC response
      // Handle both array and object response formats
      const responseData = Array.isArray(rpcData) ? rpcData[0] : rpcData;
      const rawRecalls = responseData?.data || [];
      const totalCount = responseData?.total_count || 0;

      // Check if the data needs expansion (has safety_recalls arrays)
      // The RPC might return rows with safety_recalls JSON arrays that need to be expanded
      const needsExpansion =
        rawRecalls.length > 0 &&
        rawRecalls[0]?.safety_recalls &&
        Array.isArray(rawRecalls[0].safety_recalls);

      let recalls: RecallVehicle[];
      const actualTotalCount = totalCount;

      if (needsExpansion) {
        // Expand safety_recalls array - each car row becomes multiple recall rows
        // This matches the behavior of transformCarsWithSafetyRecallsToAppFormat
        const { transformCarsWithSafetyRecallsToAppFormat } = await import(
          '@/lib/supabase/transform'
        );

        // Get campaign preferences if userId is provided (for priority and mobile_eligible)
        let campaignPreferences:
          | Record<string, { mobile_eligible?: boolean; skill_level?: number }>
          | undefined;
        if (userId) {
          try {
            const prodClient = getRecallClient();
            const { getTenantIdFromSession } = await import('@/lib/supabase/tenantScoping');
            const tenantIdForPrefs = await getTenantIdFromSession(prodClient);

            if (tenantIdForPrefs) {
              const { data: prefsData } = await prodClient
                .from('user_preferences')
                .select('entity_id, preference_value')
                .eq('user_id', userId)
                .eq('preference_type', 'campaign')
                .eq('tenant_id', tenantIdForPrefs);

              if (prefsData) {
                campaignPreferences = {};
                prefsData.forEach((pref: any) => {
                  const prefValue =
                    typeof pref.preference_value === 'string'
                      ? JSON.parse(pref.preference_value)
                      : pref.preference_value;
                  campaignPreferences![pref.entity_id] = {
                    mobile_eligible: prefValue?.mobile_eligible,
                    skill_level: prefValue?.skill_level
                  };
                });
              }
            }
          } catch (error) {
            // If campaign preferences fail to load, continue without them
            console.warn('[RecallList] Failed to load campaign preferences:', error);
          }
        }

        // Use the transform function to expand safety_recalls arrays
        recalls = transformCarsWithSafetyRecallsToAppFormat(
          rawRecalls,
          campaignPreferences
        ) as RecallVehicle[];

        // Apply filters and pagination after expansion (both internal and dealer tenants)
        if ((responseData as any)?._internalTenantFilters) {
          const internalFilters = (responseData as any)._internalTenantFilters as RecallFilters;

          // Deduplicate by VIN + campaign_number, keeping latest snapshot_date
          // Same recall can appear in multiple zones - keep only the most recent
          const recallMap = new Map<string, RecallVehicle>();
          recalls.forEach(recall => {
            const key = `${recall.vin}-${recall.campaign_number}`;
            const existing = recallMap.get(key);
            if (!existing) {
              recallMap.set(key, recall);
            } else {
              // Compare snapshot_date - keep the one with latest date
              const existingDate = (existing as any).snapshot_date;
              const newDate = (recall as any).snapshot_date;
              if (newDate && (!existingDate || newDate > existingDate)) {
                recallMap.set(key, recall);
              }
            }
          });
          recalls = Array.from(recallMap.values());

          // Exclude completed/cancelled recalls (viewport RPC has no server-side exclusion)
          const excludedPairs = await getExcludedRecallPairs(prodClient);
          recalls = recalls.filter(r => !excludedPairs.has(`${r.vin}-${r.campaign_number}`));

          // Apply filters
          if (internalFilters.vin) {
            recalls = recalls.filter(r =>
              r.vin?.toLowerCase().includes(internalFilters.vin!.toLowerCase())
            );
          }
          if (internalFilters.make) {
            recalls = recalls.filter(
              r =>
                r.make?.toLowerCase().includes(internalFilters.make!.toLowerCase()) ||
                r.makeName?.toLowerCase().includes(internalFilters.make!.toLowerCase())
            );
          }
          if (internalFilters.model) {
            recalls = recalls.filter(
              r =>
                r.model?.toLowerCase().includes(internalFilters.model!.toLowerCase()) ||
                r.modelName?.toLowerCase().includes(internalFilters.model!.toLowerCase())
            );
          }
          if (internalFilters.year) {
            recalls = recalls.filter(
              r => r.year === internalFilters.year || r.carYear === internalFilters.year
            );
          }
          if (internalFilters.dealer) {
            recalls = recalls.filter(
              r =>
                r.seller_name?.toLowerCase().includes(internalFilters.dealer!.toLowerCase()) ||
                (r as RecallVehicle & { dealer?: string }).dealer
                  ?.toLowerCase()
                  .includes(internalFilters.dealer!.toLowerCase())
            );
          }
          const campaignFilterList =
            (internalFilters.campaigns?.length ?? 0) > 0
              ? internalFilters.campaigns
              : internalFilters.campaign
                ? [internalFilters.campaign]
                : null;
          if (campaignFilterList) {
            recalls = recalls.filter(r => campaignFilterList.includes(r.campaign_number));
          }
          if (internalFilters.location?.city) {
            recalls = recalls.filter(
              r =>
                r.seller_city
                  ?.toLowerCase()
                  .includes(internalFilters.location!.city!.toLowerCase()) ||
                r.dealer_city?.toLowerCase().includes(internalFilters.location!.city!.toLowerCase())
            );
          }
          if (internalFilters.location?.zipCode) {
            recalls = recalls.filter(
              r =>
                r.seller_postal_code?.includes(internalFilters.location!.zipCode!) ||
                r.dealer_postal_code?.includes(internalFilters.location!.zipCode!)
            );
          }

          // Update total count after filtering
          const filteredTotal = recalls.length;

          // Apply pagination
          const offset = (page - 1) * cappedPageSize;
          recalls = recalls.slice(offset, offset + cappedPageSize);

          // Return with updated counts
          return {
            recalls,
            total: filteredTotal,
            page: page,
            pageSize: cappedPageSize,
            totalPages: Math.ceil(filteredTotal / cappedPageSize)
          };
        }

        // If we expanded client-side, the total count from RPC might be for cars, not recalls
        // We need to get the actual total count of recalls
        // For now, we'll use the expanded count as a fallback, but ideally the RPC should handle this
        // Note: This is a limitation - we'd need to query all pages to get the true total
        // For pagination to work correctly, the RPC should expand server-side
        // If the RPC returns total_count, it should be the count after expansion
        // If not, we'll need to handle pagination differently
      } else {
        // Data is already expanded, just map it to ensure proper field names
        recalls = rawRecalls.map((recall: any) => {
          // CRITICAL: Map campaign_number and recall_title BEFORE spreading
          // The RPC returns these fields, so preserve them if they exist
          const campaignNumber = recall.campaign_number || recall.campaign || '';
          const recallTitle =
            recall.recall_title ||
            recall.title ||
            recall.description ||
            recall.campaign_title ||
            '';

          // Handle advance_notice flag - prioritize individual recall's flag over car-level flag
          // The car-level has_advance_notice_recalls flag indicates if ANY recall has advance
          // notice, but we need the flag for THIS specific recall
          let hasAdvanceNotice = false;
          // First check individual recall's advance_notice flag (most accurate)
          if (recall.advance_notice !== undefined) {
            if (typeof recall.advance_notice === 'boolean') {
              hasAdvanceNotice = recall.advance_notice;
            } else if (typeof recall.advance_notice === 'string') {
              hasAdvanceNotice = recall.advance_notice.toUpperCase().trim() === 'Y';
            }
          } else if (recall.has_advance_notice_recalls !== undefined) {
            // Fallback to car-level flag only if individual flag is not available
            // This is less accurate but better than nothing
            hasAdvanceNotice = recall.has_advance_notice_recalls === true;
          }

          const mapped = {
            ...recall,
            // Explicitly set these fields to ensure they're present
            campaign_number: campaignNumber,
            recall_title: recallTitle,
            // Ensure advance_notice is properly set from individual recall, not car-level
            has_advance_notice_recalls: hasAdvanceNotice,
            // Ensure legacy compatibility fields are set (preserve existing if present)
            make: recall.make ?? recall.makeName ?? '',
            model: recall.model ?? recall.modelName ?? '',
            year: recall.year ?? recall.carYear ?? 0,
            // Map other fields that might have different names
            makeName: recall.makeName ?? recall.make ?? '',
            modelName: recall.modelName ?? recall.model ?? '',
            carYear: recall.carYear ?? recall.year ?? 0
          };

          return mapped;
        }) as RecallVehicle[];

        // Apply filters and pagination after mapping (both internal and dealer tenants)
        if ((responseData as any)?._internalTenantFilters) {
          const internalFilters = (responseData as any)._internalTenantFilters as RecallFilters;

          // Deduplicate by VIN + campaign_number, keeping latest snapshot_date
          // Same recall can appear in multiple zones - keep only the most recent
          const recallMap = new Map<string, RecallVehicle>();
          recalls.forEach(recall => {
            const key = `${recall.vin}-${recall.campaign_number}`;
            const existing = recallMap.get(key);
            if (!existing) {
              recallMap.set(key, recall);
            } else {
              // Compare snapshot_date - keep the one with latest date
              const existingDate = (existing as any).snapshot_date;
              const newDate = (recall as any).snapshot_date;
              if (newDate && (!existingDate || newDate > existingDate)) {
                recallMap.set(key, recall);
              }
            }
          });
          recalls = Array.from(recallMap.values());

          // Exclude completed/cancelled recalls (viewport RPC has no server-side exclusion)
          const excludedPairs2 = await getExcludedRecallPairs(prodClient);
          recalls = recalls.filter(r => !excludedPairs2.has(`${r.vin}-${r.campaign_number}`));

          // Apply filters
          if (internalFilters.vin) {
            recalls = recalls.filter(r =>
              r.vin?.toLowerCase().includes(internalFilters.vin!.toLowerCase())
            );
          }
          if (internalFilters.make) {
            recalls = recalls.filter(
              r =>
                r.make?.toLowerCase().includes(internalFilters.make!.toLowerCase()) ||
                r.makeName?.toLowerCase().includes(internalFilters.make!.toLowerCase())
            );
          }
          if (internalFilters.model) {
            recalls = recalls.filter(
              r =>
                r.model?.toLowerCase().includes(internalFilters.model!.toLowerCase()) ||
                r.modelName?.toLowerCase().includes(internalFilters.model!.toLowerCase())
            );
          }
          if (internalFilters.year) {
            recalls = recalls.filter(
              r => r.year === internalFilters.year || r.carYear === internalFilters.year
            );
          }
          if (internalFilters.dealer) {
            recalls = recalls.filter(
              r =>
                r.seller_name?.toLowerCase().includes(internalFilters.dealer!.toLowerCase()) ||
                (r as RecallVehicle & { dealer?: string }).dealer
                  ?.toLowerCase()
                  .includes(internalFilters.dealer!.toLowerCase())
            );
          }
          const campaignFilterList =
            (internalFilters.campaigns?.length ?? 0) > 0
              ? internalFilters.campaigns
              : internalFilters.campaign
                ? [internalFilters.campaign]
                : null;
          if (campaignFilterList) {
            recalls = recalls.filter(r => campaignFilterList.includes(r.campaign_number));
          }
          if (internalFilters.location?.city) {
            recalls = recalls.filter(
              r =>
                r.seller_city
                  ?.toLowerCase()
                  .includes(internalFilters.location!.city!.toLowerCase()) ||
                r.dealer_city?.toLowerCase().includes(internalFilters.location!.city!.toLowerCase())
            );
          }
          if (internalFilters.location?.zipCode) {
            recalls = recalls.filter(
              r =>
                r.seller_postal_code?.includes(internalFilters.location!.zipCode!) ||
                r.dealer_postal_code?.includes(internalFilters.location!.zipCode!)
            );
          }

          // Update total count after filtering
          const filteredTotal = recalls.length;

          // Apply pagination
          const offset = (page - 1) * cappedPageSize;
          recalls = recalls.slice(offset, offset + cappedPageSize);

          // Return with updated counts
          return {
            recalls,
            total: filteredTotal,
            page: page,
            pageSize: cappedPageSize,
            totalPages: Math.ceil(filteredTotal / cappedPageSize)
          };
        }
      }

      return {
        recalls,
        total: actualTotalCount,
        page: responseData?.page || page,
        pageSize: responseData?.page_size || cappedPageSize,
        totalPages:
          responseData?.total_pages ||
          Math.ceil(actualTotalCount / (responseData?.page_size || cappedPageSize))
      };
    } catch (error: any) {
      // Handle rate limit and session errors specially
      if (error.name === 'RateLimitError' || error instanceof TenantAccessError) {
        throw error;
      }
      throw new Error(`Failed to fetch recalls: ${error.message}`);
    }
  },

  // Note: Removed old methods loadInitialRecallData, loadMoreRecallData, and getRecallListWithClientFiltering
  // These are replaced by the secure RPC function get_paginated_recalls_secure

  // Get recall statistics for dashboard
  // eslint-disable-next-line no-unused-vars -- reserved for future rate limiting
  async getRecallStats(_userId?: string): Promise<RecallStats> {
    const prodClient = getRecallClient();
    const { getTenantIdFromSession } = await import('@/lib/supabase/tenantScoping');
    const tenantId = await getTenantIdFromSession(prodClient);

    if (!tenantId) {
      // Return empty stats if no tenant
      return {
        totalRecalls: 0,
        byPriority: { 1: 0, 2: 0, 3: 0 },
        byMake: {},
        byRegion: {},
        mobileEligible: 0,
        recentRecalls: 0
      };
    }

    // For stats, we'll use the paginated function with a reasonable limit
    const { data: result, error: statsError } = await prodClient.rpc(
      'get_paginated_recalls_secure',
      {
        p_tenant_id: tenantId,
        p_filters: {},
        p_page: 1,
        p_page_size: 100 // Get first 100 for stats calculation
      }
    );
    if (isTenantAccessError(statsError)) throw new TenantAccessError();

    if (!result || !result.data) {
      return {
        totalRecalls: 0,
        byPriority: { 1: 0, 2: 0, 3: 0 },
        byMake: {},
        byRegion: {},
        mobileEligible: 0,
        recentRecalls: 0
      };
    }

    // Calculate statistics from the sample
    const recalls = result.data || [];
    const stats: RecallStats = {
      totalRecalls: result.total_count || 0, // Use total from server
      byPriority: { 1: 0, 2: 0, 3: 0 },
      byMake: {},
      byRegion: {},
      mobileEligible: 0,
      recentRecalls: 0
    };

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    recalls.forEach((recall: any) => {
      // Priority distribution
      const priority = recall.priority || 3;
      stats.byPriority[priority] = (stats.byPriority[priority] || 0) + 1;

      // Make distribution
      const make = recall.makeName || 'Unknown';
      stats.byMake[make] = (stats.byMake[make] || 0) + 1;

      // Region distribution
      const region = recall.dealer_region || 'Unknown';
      stats.byRegion[region] = (stats.byRegion[region] || 0) + 1;

      // Mobile eligibility
      if (recall.mobile_eligible) {
        stats.mobileEligible++;
      }

      // Recent recalls (last 30 days)
      const recallDate = new Date(recall.recall_date);
      if (recallDate >= thirtyDaysAgo) {
        stats.recentRecalls++;
      }
    });

    // Scale up the sample stats to match total count
    const scaleFactor = stats.totalRecalls / recalls.length;
    if (scaleFactor > 1) {
      stats.mobileEligible = Math.round(stats.mobileEligible * scaleFactor);
      stats.recentRecalls = Math.round(stats.recentRecalls * scaleFactor);

      Object.keys(stats.byPriority).forEach(key => {
        const priority = parseInt(key);
        stats.byPriority[priority] = Math.round((stats.byPriority[priority] ?? 0) * scaleFactor);
      });
    }

    return stats;
  },

  // Get unique values for filter dropdowns
  // Get filter options (for dropdowns) - optimized with proper error handling
  async getFilterOptions(): Promise<{
    makes: string[];
    models: string[];
    years: number[];
    dealers: string[];
    regions: string[];
    campaigns: string[];
    cities: string[];
    zipCodes: string[];
  }> {
    try {
      const prodClient = getRecallClient();

      // Get tenant ID for tenant scoping
      const { getTenantIdFromSession } = await import('@/lib/supabase/tenantScoping');
      const tenantId = await getTenantIdFromSession(prodClient);

      if (!tenantId) {
        console.warn(
          '[recallAPI.getFilterOptions] No tenant ID available, returning empty options'
        );
        return {
          makes: [],
          models: [],
          years: [],
          dealers: [],
          regions: [],
          campaigns: [],
          cities: [],
          zipCodes: []
        };
      }

      // Call the optimized RPC function
      const { data, error } = await prodClient.rpc('get_recall_filter_options_secure', {
        p_tenant_id: tenantId
      });

      if (error) {
        // A session/tenant mismatch is not "no filter options".
        if (isTenantAccessError(error)) throw new TenantAccessError();
        console.error('[recallAPI.getFilterOptions] RPC error:', error);
        // Return empty arrays instead of throwing to prevent UI breakage
        return {
          makes: [],
          models: [],
          years: [],
          dealers: [],
          regions: [],
          campaigns: [],
          cities: [],
          zipCodes: []
        };
      }

      // Handle both array and object response formats
      const result = Array.isArray(data) ? data[0] : data;

      if (!result) {
        console.warn('[recallAPI.getFilterOptions] No data returned from RPC');
        return {
          makes: [],
          models: [],
          years: [],
          dealers: [],
          regions: [],
          campaigns: [],
          cities: [],
          zipCodes: []
        };
      }

      // Return filter options with proper field mapping from MV-backed RPC
      return {
        makes: Array.isArray(result.makes)
          ? result.makes.filter((m: string) => m && m.trim() !== '')
          : [],
        models: Array.isArray(result.models)
          ? result.models.filter((m: string) => m && m.trim() !== '')
          : [],
        years: Array.isArray(result.years) ? result.years : [],
        dealers: Array.isArray(result.dealers)
          ? result.dealers.filter((d: string) => d && d.trim() !== '')
          : [],
        regions: Array.isArray(result.regions)
          ? result.regions.filter((r: string) => r && r.trim() !== '')
          : [],
        campaigns: Array.isArray(result.campaigns)
          ? result.campaigns.filter((c: string) => c && c.trim() !== '')
          : [],
        cities: Array.isArray(result.cities)
          ? result.cities.filter((c: string) => c && c.trim() !== '')
          : [],
        zipCodes: Array.isArray(result.zip_codes)
          ? result.zip_codes.filter((z: string) => z && z.trim() !== '')
          : []
      };
    } catch (error: any) {
      if (error instanceof TenantAccessError) throw error;
      // Log error but return empty arrays to prevent UI breakage
      console.error('[recallAPI.getFilterOptions] Unexpected error:', error);
      return {
        makes: [],
        models: [],
        years: [],
        dealers: [],
        regions: [],
        campaigns: [],
        cities: [],
        zipCodes: []
      };
    }
  },

  // Export recalls to CSV
  async exportRecallsToCSV(
    filters: RecallFilters = {},
    userId: string | undefined,
    tenantId: string
  ): Promise<string> {
    // Temporarily disable rate limiting for export operations
    // const { checkRateLimit, RateLimitConfigs } = await import('@/lib/utils/rateLimiter');
    // if (userId) {
    //   await checkRateLimit(userId, RateLimitConfigs.export);
    // }

    // Get all matching recalls (capped at 1000 for export)
    const response = await this.getRecallList(filters, 1, 1000, userId, tenantId);

    // Create CSV headers
    const headers = [
      'VIN',
      'Make',
      'Model',
      'Year',
      'Dealer',
      'Address',
      'City',
      'Region',
      'ZIP',
      'Phone',
      'Campaign Number',
      'Campaign Title',
      'Priority',
      'Mobile Eligible',
      'Recall Date'
    ];

    // Create CSV rows
    const rows = response.recalls.map(recall => [
      recall.vin,
      recall.make,
      recall.model,
      recall.year,
      recall.seller_name,
      recall.seller_address,
      recall.seller_city,
      recall.seller_region,
      recall.seller_postal_code,
      recall.dealer_phone,
      recall.campaign_number,
      recall.campaign_title,
      recall.priority,
      recall.mobile_eligible ? 'Yes' : 'No',
      recall.recall_date
    ]);

    // Combine headers and rows
    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `recalls_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return 'Export completed successfully';
  }
};
